import React, { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import L from "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Chip from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { useMaterialUIController } from "context";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { openAppConfirm } from "../../components/AppDialogs";
import Footer from "examples/Footer";
import {
  listProfessorTitlesCall,
  createProfessorTitleCall,
  updateProfessorTitleCall,
  deleteProfessorTitleCall,
  listQuestionsCall,
  createQuestionCall,
  updateQuestionCall,
  deleteQuestionCall,
  listQuizAssignmentsCall,
  createQuizAssignmentCall,
  updateQuizAssignmentCall,
  deleteQuizAssignmentCall,
} from "../../redux/api/quizSystem.api";
import { GetTitlesCall } from "../../redux/api/title.api";
import { UploadMyFileCall } from "../../redux/api/uploads.api";
import ProfSelector from "components/ProfSelector";
import { RequestDemoDialog } from "components/RequestDemo";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import { generateQuestionCall } from "../../redux/api/ai.api";
import { store } from "../../redux/store";
const DIFFICULTIES = ["easy", "medium", "hard"];
const QUESTION_TYPES = [
  { value: "text_only", label: "Text only" },
  { value: "image_based", label: "Image based" },
  { value: "slide_referenced", label: "Slide or 3D model" },
];

const INPUT_HEIGHT_SX = {
  "& .MuiInputBase-root": {
    minHeight: 44,
  },
};

const noImageUrl = require("../../assets/images/no-image.jpg");

const QUIZ_RULES_EDITOR_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "blockquote", "code-block"],
    ["clean"],
  ],
};

const QUIZ_RULES_EDITOR_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "list",
  "bullet",
  "link",
  "blockquote",
  "code-block",
];

function getTitleLabel(item, field = "title") {
  const list = item?.titleLocalizations?.filter((e) => e.field === field) || [];
  return list[0]?.value || "";
}

function getSlideImageUrl(subtitle) {
  if (subtitle?.image) return subtitle.image;
  const slide = subtitle?.slides?.[0];
  const file = slide?.Files?.[0] || slide?.files?.[0];
  const url = file?.url;
  if (url) return url + "/3/3/3.png";
  return null;
}

function getSlideTileUrl(entry) {
  if (!entry?.imageUrl) return "";
  return entry.imageUrl.replace(/\/3\/3\/3\.png$/i, "") + "/{z}/{x}/{y}.png";
}

function getPinLabel(index) {
  const labels = ["A", "B", "C", "D"];
  return labels[index] ?? String.fromCharCode(65 + index);
}

function createSlidePinIcon(label) {
  return L.divIcon({
    className: "slide-pin-marker-wrapper",
    html: `<div style="display:flex;flex-direction:column;align-items:center;line-height:1;pointer-events:auto;">
      <div style="width:0;height:0;border-left:8px solid transparent;border-right:8px solid transparent;border-top:14px solid #28a745;"></div>
      <div style="background:#007bff;color:white;font-weight:bold;font-size:13px;border-radius:4px;padding:3px 8px;margin-top:2px;min-width:22px;text-align:center;box-sizing:border-box;">${label}</div>
    </div>`,
    iconSize: [24, 44],
    iconAnchor: [12, 0],
  });
}

function generateUniqueId() {
  return "id-" + Math.random().toString(36).substr(2, 16);
}

function hasMeaningfulHtmlContent(html) {
  if (!html || typeof html !== "string") return false;
  const textOnly = html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return textOnly.length > 0;
}

function buildSlideEntries(titles, sourceLabel) {
  const entries = [];
  const is3D = sourceLabel === "3D Model";
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub) => {
      const slide = sub?.slides?.[0] || sub?.referencedSlides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const fileUrl = file?.url ?? file?.path;
      entries.push({
        tileId: tile.id,
        slideId: slide.id,
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        imageUrl: getSlideImageUrl(sub),
        source: sourceLabel,
        is3D: !!is3D,
        modelUrl: is3D && fileUrl ? (typeof fileUrl === "string" ? fileUrl.trim() : null) : null,
      });
    });
  });
  return entries;
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function QuizSystem({ stage }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [controller] = useMaterialUIController();
  const darkMode = controller?.darkMode === true;
  const auth = useSelector((state) => state?.auth);
  const universityId = auth?.university_id ?? auth?.universityInfo?.id;
  const role = auth?.role;
  const authUserId = auth?.id;
  const isProfRole = role === "prof" || role === "professor";
  const canSelectProf = role === "admin" || role === "university_admin";
  const showRequestDemoCta = isProfRole || role === "university_admin";
  const isStandaloneStage = typeof stage === "number" && stage >= 0 && stage <= 2;
  const [tab, setTab] = useState(isStandaloneStage ? stage : 0);
  const [professorTitlesTree, setProfessorTitlesTree] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [questionDialog, setQuestionDialog] = useState(false);
  const [quizDialog, setQuizDialog] = useState(false);
  const [titleDialog, setTitleDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [editingTitle, setEditingTitle] = useState(null);
  const [titleForm, setTitleForm] = useState({ title: "", parent_id: "" });
  const [filters, setFilters] = useState({ professor_topic_id: "", professor_tile_id: "", difficulty: "" });
  const [selectedProfId, setSelectedProfId] = useState(isProfRole && authUserId ? authUserId : "");
  const [questionsPage, setQuestionsPage] = useState(0);
  const [questionsRowsPerPage, setQuestionsRowsPerPage] = useState(10);
  const [assignmentsPage, setAssignmentsPage] = useState(0);
  const [assignmentsRowsPerPage, setAssignmentsRowsPerPage] = useState(10);
  const [publicSlideEntries, setPublicSlideEntries] = useState([]);
  const [slideMapReady, setSlideMapReady] = useState(false);
  const [dialogTransitionDone, setDialogTransitionDone] = useState(false);
  const slideMapRef = useRef(null);
  const slideMapMarkersRef = useRef([]);
  const slideMapPinCountRef = useRef(0);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiAssistExpanded, setAiAssistExpanded] = useState(true);
  const [assignmentMenuAnchor, setAssignmentMenuAnchor] = useState(null);
  const [assignmentMenuRow, setAssignmentMenuRow] = useState(null);
  const [questionRowMenuAnchor, setQuestionRowMenuAnchor] = useState(null);
  const [questionRowMenuQuestion, setQuestionRowMenuQuestion] = useState(null);
  const [questionImageUploading, setQuestionImageUploading] = useState(false);
  const questionImageInputRef = useRef(null);
  const [questionForm, setQuestionForm] = useState({
    title: "",
    question_type: "text_only",
    difficulty: "medium",
    professor_topic_id: "",
    professor_tile_id: "",
    tile_id: "",
    slide_id: "",
    question_content: "",
    question_image_url: "",
    pins: [],
    explanation: "",
    answers: [
      { answer_text: "", answer_image_url: "", is_correct: false },
      { answer_text: "", answer_image_url: "", is_correct: false },
      { answer_text: "", answer_image_url: "", is_correct: false },
      { answer_text: "", answer_image_url: "", is_correct: false },
    ],
  });
  const [quizForm, setQuizForm] = useState({
    title: "",
    time_limit_minutes: 30,
    shuffle_questions: true,
    shuffle_answers: true,
    show_score_immediately: true,
    show_answers_after: true,
    // Optional HTML block shown as quiz rules to students (mobile & web).
    // Default template can be customized per quiz.
    rules_html: `<h2>Quiz Rules</h2>
<p>Please read the following instructions carefully before starting:</p>
<ol>
  <li><strong>No Cheating</strong><br/>Any form of cheating is strictly prohibited, including capturing content using phones, screenshots, or other devices.</li>
  <li><strong>No External Assistance</strong><br/>You must complete the quiz independently without using notes, resources, or help from others.</li>
  <li><strong>Academic Integrity</strong><br/>All participants are expected to follow academic integrity guidelines at all times.</li>
  <li><strong>Time Limit</strong><br/>Each quiz must be completed within the allocated time. The timer runs continuously and will not pause, even if you leave the app or exit the quiz.</li>
  <li><strong>Auto-Submission</strong><br/>The quiz will be automatically submitted once the time expires.</li>
  <li><strong>No Backtracking</strong><br/>Once a question is answered, you cannot return to it.</li>
  <li><strong>Single Attempt</strong><br/>Each quiz can only be taken once unless specified otherwise.</li>
  <li><strong>Full Attention Required</strong><br/>Leaving the quiz screen may affect your progress or lead to automatic submission.</li>
  <li><strong>Stable Connection</strong><br/>Ensure you have a reliable internet connection before starting the quiz.</li>
  <li><strong>Question Format</strong><br/>Questions may include text, images, histology slides, or 3D models, and are primarily multiple-choice.</li>
</ol>
<p><em>By starting this quiz, you confirm that you understand and agree to these rules.</em></p>`,
    // Quiz rules are per topic (professor_topic_id), not per tile
    topic_rules: [{ professor_topic_id: "", num_questions: 2, difficulty_filter: "" }],
  });

  const [requestDemoOpen, setRequestDemoOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrDialogCode, setQrDialogCode] = useState("");
  const [qrDialogLink, setQrDialogLink] = useState("");

  // Always use app custom scheme for QR so scanning opens the app (not Safari).
  // HTTPS (e.g. REACT_APP_MOBILE_QUIZ_DEEP_LINK_BASE) only works with Universal/App Links setup.
  const buildQuizDeepLink = (code) => {
    if (!code) return "";
    return `microlab://quiz/join?code=${encodeURIComponent(code)}`;
  };

  const openQrDialogForCode = (code) => {
    if (!code) return;
    const link = buildQuizDeepLink(code);
    setQrDialogCode(code);
    setQrDialogLink(link);
    setQrDialogOpen(true);
  };

  /** Normalize API tree: backend returns sub_titles, frontend uses children */
  const normalizeProfessorTitlesTree = (nodes) => {
    if (!Array.isArray(nodes)) return [];
    return nodes.map((t) => {
      const childList = t.children ?? t.sub_titles ?? [];
      return { ...t, children: normalizeProfessorTitlesTree(childList) };
    });
  };

  const flattenProfessorTitles = (items, parent = null) => {
    let out = [];
    (items || []).forEach((t) => {
      out.push({ id: t.id, title: t.title, parent_id: t.parent_id, isTile: !t.parent_id });
      if (t.children?.length) {
        out = out.concat(flattenProfessorTitles(t.children, t));
      }
    });
    return out;
  };

  const activeTab = isStandaloneStage ? stage : tab;

  const pageTitle = (() => {
    if (isStandaloneStage) {
      if (stage === 0) return t("pages.quiz.myTitles");
      if (stage === 1) return t("pages.quiz.questionBank");
      if (stage === 2) return t("pages.quiz.createQuiz");
    }
    return t("nav.quiz");
  })();

  const pageSubtitle = (() => {
    if (isStandaloneStage) {
      if (stage === 0) return "Organize quiz titles and topics used by your questions and quizzes.";
      if (stage === 1) return "Manage your question bank with filters by topic, title, and difficulty.";
      if (stage === 2) return "Create and configure quizzes using your existing questions and topics.";
    }
    return t("pages.quiz.questionBank") + " and " + t("pages.quiz.createQuiz");
  })();

  const loadProfessorTitles = async () => {
    const params = selectedProfId ? { prof_id: selectedProfId } : {};
    const res = await listProfessorTitlesCall(params);
    const raw = Array.isArray(res?.data) ? res.data : [];
    setProfessorTitlesTree(normalizeProfessorTitlesTree(raw));
  };

  useEffect(() => {
    loadProfessorTitles();
  }, [activeTab, selectedProfId]);

  const flatProfTitles = flattenProfessorTitles(professorTitlesTree);
  const profTiles = flatProfTitles.filter((t) => t.isTile);
  // Topic = nested topics when present; otherwise show root-level titles so dropdown is never empty
  const nestedTopics = flatProfTitles.filter((t) => !t.isTile);
  const profTopics = nestedTopics.length > 0 ? nestedTopics : flatProfTitles;

  const loadQuestions = async () => {
    setLoading(true);
    const params = {};
    if (filters.professor_topic_id) params.professor_topic_id = filters.professor_topic_id;
    if (filters.professor_tile_id) params.professor_tile_id = filters.professor_tile_id;
    if (filters.difficulty) params.difficulty = filters.difficulty;
    if (selectedProfId) params.prof_id = selectedProfId;
    const res = await listQuestionsCall(params);
    setQuestions(Array.isArray(res?.data) ? res.data : []);
    setLoading(false);
  };

  const loadAssignments = async () => {
    setLoading(true);
    const params = selectedProfId ? { prof_id: selectedProfId } : {};
    const res = await listQuizAssignmentsCall(params);
    setAssignments(Array.isArray(res?.data) ? res.data : []);
    setLoading(false);
  };

  useEffect(() => {
    if (activeTab === 1) loadQuestions();
    else if (activeTab === 2) loadAssignments();
  }, [activeTab, filters.professor_topic_id, filters.professor_tile_id, filters.difficulty, selectedProfId]);

  useEffect(() => {
    setQuestionsPage(0);
  }, [filters.professor_topic_id, filters.professor_tile_id, filters.difficulty]);

  useEffect(() => {
    if (!questionDialog) return;
    const lang = store.getState().general?.lang || "en";
    const load = async () => {
      try {
        const [hisRes, embRes, threeRes] = await Promise.all([
          GetTitlesCall(lang, "his"),
          GetTitlesCall(lang, "emb"),
          GetTitlesCall(lang, "three_d_model"),
        ]);
        const hisTitles = Array.isArray(hisRes) ? hisRes : [];
        const embTitles = Array.isArray(embRes) ? embRes : [];
        const threeTitles = Array.isArray(threeRes) ? threeRes : [];
        setPublicSlideEntries([
          ...buildSlideEntries(hisTitles, "Histology"),
          ...buildSlideEntries(embTitles, "Embryology"),
          ...buildSlideEntries(threeTitles, "3D Model"),
        ]);
      } catch {
        setPublicSlideEntries([]);
      }
    };
    load();
  }, [questionDialog]);

  const selectedSlideEntry = useMemo(
    () =>
      publicSlideEntries.find(
        (e) =>
          e.tileId === questionForm.tile_id && e.slideId === questionForm.slide_id
      ),
    [publicSlideEntries, questionForm.tile_id, questionForm.slide_id]
  );

  const looksLike3DUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const path = url.split("?")[0].toLowerCase();
    return path.endsWith(".glb") || path.endsWith(".gltf");
  };
  const is3DSelected =
    selectedSlideEntry?.is3D || looksLike3DUrl(questionForm.question_image_url);
  const slideTileUrl =
    questionForm.question_type === "slide_referenced" &&
      questionForm.question_image_url &&
      !is3DSelected
      ? questionForm.question_image_url
      : "";

  useLayoutEffect(() => {
    if (!questionDialog || !slideTileUrl) {
      setSlideMapReady(false);
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideMapMarkersRef.current = [];
      slideMapPinCountRef.current = 0;
      return;
    }
    if (!dialogTransitionDone) return;
    const mapContainerId = "question-bank-slide-map";
    const mapEl = document.getElementById(mapContainerId);
    if (!mapEl || slideMapRef.current) return;
    const urlToUse = slideTileUrl;
    const t = setTimeout(() => {
      const el = document.getElementById(mapContainerId);
      if (!el || slideMapRef.current || !urlToUse) return;
      el.innerHTML = "";
      const map = L.map(mapContainerId, {
        maxZoom: 10,
        maxBounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
        maxBoundsViscosity: 1,
      }).setView([-10.505, -0.09], 2);
      slideMapRef.current = map;
      L.tileLayer(urlToUse, {
        attribution: "&copy; Microlab",
        maxZoom: 10,
        noWrap: true,
        bounds: L.latLngBounds(L.latLng(-85, -180), L.latLng(85, 180)),
      }).addTo(map);
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      const drawControl = new L.Control.Draw({
        edit: { featureGroup: drawnItems },
      });
      map.addControl(drawControl);
      map.on("draw:created", (e) => {
        if (e.layerType !== "marker") return;
        const latlng = e.layer.getLatLng();
        e.layer.remove();
        const labelIndex = slideMapPinCountRef.current;
        const label = getPinLabel(labelIndex);
        const marker = L.marker(latlng, { icon: createSlidePinIcon(label) }).addTo(drawnItems);
        const geoJsonItem = {
          type: "Feature",
          geometry: { type: "Point", coordinates: [latlng.lng, latlng.lat] },
          properties: { id: generateUniqueId(), label },
        };
        setQuestionForm((prev) => ({ ...prev, pins: [...(prev.pins || []), geoJsonItem] }));
        slideMapMarkersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
        slideMapPinCountRef.current += 1;
      });
      setSlideMapReady(true);
      requestAnimationFrame(() => {
        if (slideMapRef.current) slideMapRef.current.invalidateSize();
      });
      setTimeout(() => {
        if (slideMapRef.current) slideMapRef.current.invalidateSize();
      }, 100);
      setTimeout(() => {
        if (slideMapRef.current) slideMapRef.current.invalidateSize();
      }, 400);
    }, 150);
    return () => {
      clearTimeout(t);
      setSlideMapReady(false);
      if (slideMapRef.current) {
        slideMapRef.current.remove();
        slideMapRef.current = null;
      }
      slideMapMarkersRef.current = [];
    };
  }, [questionDialog, slideTileUrl, dialogTransitionDone]);

  useEffect(() => {
    if (!slideMapRef.current || !slideMapReady) return;
    const pins = questionForm.pins || [];
    if (pins.length === 0) {
      slideMapMarkersRef.current.forEach((m) => m.marker.remove());
      slideMapMarkersRef.current = [];
      slideMapPinCountRef.current = 0;
      return;
    }
    slideMapMarkersRef.current.forEach((m) => m.marker.remove());
    slideMapMarkersRef.current = [];
    pins.forEach((geoJsonItem) => {
      const [lng, lat] = geoJsonItem.geometry.coordinates;
      const label = geoJsonItem.properties.label;
      const marker = L.marker([lat, lng], { icon: createSlidePinIcon(label) }).addTo(slideMapRef.current);
      slideMapMarkersRef.current.push({ marker, geoJsonId: geoJsonItem.properties.id });
    });
    slideMapPinCountRef.current = pins.length;
    // Only fitBounds when we have 2+ pins; with 1 pin fitBounds zooms to max and hides the slide tiles
    if (pins.length >= 2) {
      const bounds = L.latLngBounds(pins.map((f) => [f.geometry.coordinates[1], f.geometry.coordinates[0]]));
      if (bounds.isValid()) slideMapRef.current.fitBounds(bounds, { padding: [30, 30], maxZoom: 10 });
    }
  }, [questionForm.pins, slideMapReady]);

  const openNewQuestion = () => {
    setEditingQuestion(null);
    setDialogTransitionDone(false);
    setAiPrompt("");
    setAiError(null);
    setQuestionForm({
      title: "",
      question_type: "text_only",
      difficulty: "medium",
      professor_topic_id: "",
      professor_tile_id: "",
      tile_id: "",
      slide_id: "",
      question_content: "",
      question_image_url: "",
      pins: [],
      explanation: "",
      answers: ["a", "b", "c", "d"].map((key, i) => ({
        answer_text: "",
        answer_image_url: "",
        is_correct: i === 0,
      })),
    });
    setQuestionDialog(true);
  };

  const openNewTitle = (parentId = "") => {
    setEditingTitle(null);
    setTitleForm({ title: "", parent_id: parentId || "" });
    setTitleDialog(true);
  };

  const openEditTitle = (t) => {
    setEditingTitle(t);
    setTitleForm({ title: t.title || "", parent_id: t.parent_id || "" });
    setTitleDialog(true);
  };

  const saveTitle = async () => {
    if (!titleForm.title?.trim()) return;
    if (editingTitle) {
      await updateProfessorTitleCall(editingTitle.id, { title: titleForm.title.trim() });
    } else {
      await createProfessorTitleCall({ title: titleForm.title.trim(), parent_id: titleForm.parent_id || null });
    }
    setTitleDialog(false);
    loadProfessorTitles();
  };

  const deleteTitle = async (id) => {
    const ok = await openAppConfirm({
      title: "Delete title?",
      message: "Delete this title? Child topics must be deleted first.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    await deleteProfessorTitleCall(id);
    loadProfessorTitles();
  };

  const openEditQuestion = (q) => {
    setEditingQuestion(q);
    const answers = (q.answers || []).slice(0, 4);
    while (answers.length < 4) answers.push({ answer_text: "", answer_image_url: "", is_correct: false });
    let pins = [];
    if (q.pins != null) {
      try {
        pins = typeof q.pins === "string" ? JSON.parse(q.pins) : (Array.isArray(q.pins) ? q.pins : []);
      } catch (_) { }
    }
    const imageUrl = q.question_image_url ?? q.questionImageUrl ?? "";
    setQuestionForm({
      title: q.title || "",
      question_type: q.question_type || "text_only",
      difficulty: q.difficulty || "medium",
      professor_topic_id: q.professor_topic_id || "",
      professor_tile_id: q.professor_tile_id || "",
      tile_id: q.tile_id || "",
      slide_id: q.slide_id || "",
      question_content: q.question_content || "",
      question_image_url: imageUrl,
      pins: Array.isArray(pins) ? pins : [],
      explanation: q.explanation || "",
      answers: answers.map((a) => ({
        answer_text: a.answer_text || "",
        answer_image_url: a.answer_image_url || "",
        is_correct: !!a.is_correct,
      })),
    });
    setDialogTransitionDone(false);
    setQuestionDialog(true);
  };

  const setAnswerCorrect = (idx) => {
    setQuestionForm((prev) => ({
      ...prev,
      answers: prev.answers.map((a, i) => ({ ...a, is_correct: i === idx })),
    }));
  };

  const handleGenerateQuestion = async () => {
    setAiError(null);
    setAiLoading(true);
    try {
      const topicTitle = profTopics.find((t) => t.id === questionForm.professor_topic_id)?.title || "";
      const tileTitle = profTiles.find((t) => t.id === questionForm.professor_tile_id)?.title || "";
      const title = questionForm.title?.trim() || topicTitle || tileTitle || "";
      const slideContext = selectedSlideEntry?.label || "";
      const res = await generateQuestionCall({
        prompt: aiPrompt.trim(),
        title,
        slide_context: slideContext,
        question_type: questionForm.question_type,
      });
      if (res && typeof res.status === "number" && res.status >= 400) {
        setAiError(res?.message || "Failed to generate question");
        return;
      }
      const data = res?.data ?? res;
      const answers = Array.isArray(data.answers) ? data.answers : [];
      const padded = answers.slice(0, 4).map((a) => ({
        answer_text: a.answer_text || "",
        answer_image_url: "",
        is_correct: !!a.is_correct,
      }));
      while (padded.length < 4) {
        padded.push({ answer_text: "", answer_image_url: "", is_correct: false });
      }
      if (padded.filter((a) => a.is_correct).length !== 1) {
        padded[0].is_correct = true;
        for (let i = 1; i < padded.length; i++) padded[i].is_correct = false;
      }
      const shuffleArray = (arr) => {
        const out = [...arr];
        for (let i = out.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [out[i], out[j]] = [out[j], out[i]];
        }
        return out;
      };
      setQuestionForm((prev) => ({
        ...prev,
        question_content: data.question_content || prev.question_content,
        title: data.title ? data.title : prev.title,
        difficulty: data.difficulty || prev.difficulty,
        answers: shuffleArray(padded),
      }));
      setAiPrompt("");
    } catch (e) {
      setAiError(e?.message || "Something went wrong");
    } finally {
      setAiLoading(false);
    }
  };

  const saveQuestion = async () => {
    const payload = {
      ...questionForm,
      professor_topic_id: questionForm.professor_topic_id || undefined,
      professor_tile_id: questionForm.professor_tile_id || undefined,
      tile_id: questionForm.tile_id || undefined,
      slide_id: questionForm.slide_id || undefined,
      pins: questionForm.pins?.length ? JSON.stringify(questionForm.pins) : undefined,
      answers: questionForm.answers.map((a) => ({
        answer_text: a.answer_text,
        answer_image_url: a.answer_image_url || undefined,
        is_correct: a.is_correct,
      })),
    };
    if (editingQuestion) {
      await updateQuestionCall(editingQuestion.id, payload);
    } else {
      await createQuestionCall(payload);
    }
    setQuestionDialog(false);
    loadQuestions();
  };

  const deleteQuestion = async (id) => {
    const ok = await openAppConfirm({
      title: "Delete question?",
      message: "Delete this question?",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    await deleteQuestionCall(id);
    loadQuestions();
  };

  const openNewQuiz = () => {
    setEditingQuiz(null);
    setQuizForm({
      title: "",
      time_limit_minutes: 30,
      shuffle_questions: true,
      shuffle_answers: true,
      show_score_immediately: true,
      show_answers_after: true,
      rules_html: `<h2>Quiz Rules</h2>
              <p>Please read the following instructions carefully before starting:</p>
              <ol>
                <li><strong>No Cheating</strong><br/>Any form of cheating is strictly prohibited, including capturing content using phones, screenshots, or other devices.</li>
                <li><strong>No External Assistance</strong><br/>You must complete the quiz independently without using notes, resources, or help from others.</li>
                <li><strong>Academic Integrity</strong><br/>All participants are expected to follow academic integrity guidelines at all times.</li>
                <li><strong>Time Limit</strong><br/>Each quiz must be completed within the allocated time. The timer runs continuously and will not pause, even if you leave the app or exit the quiz.</li>
                <li><strong>Auto-Submission</strong><br/>The quiz will be automatically submitted once the time expires.</li>
                <li><strong>No Backtracking</strong><br/>Once a question is answered, you cannot return to it.</li>
                <li><strong>Single Attempt</strong><br/>Each quiz can only be taken once unless specified otherwise.</li>
                <li><strong>Full Attention Required</strong><br/>Leaving the quiz screen may affect your progress or lead to automatic submission.</li>
                <li><strong>Stable Connection</strong><br/>Ensure you have a reliable internet connection before starting the quiz.</li>
                <li><strong>Question Format</strong><br/>Questions may include text, images, histology slides, or 3D models, and are primarily multiple-choice.</li>
              </ol>
              <p><em>By starting this quiz, you confirm that you understand and agree to these rules.</em></p>`,

      topic_rules: [{
        professor_topic_id: "", num_questions: 2,
        difficulty_filter: ""
      }],
    });
    setQuizDialog(true);
  };

  const openEditQuiz = (a) => {
    setEditingQuiz(a);
    setQuizForm({
      title: a.title || "",
      time_limit_minutes: a.time_limit_minutes ?? 30,
      shuffle_questions: a.shuffle_questions !== false,
      shuffle_answers: a.shuffle_answers !== false,
      show_score_immediately: a.show_score_immediately !== false,
      show_answers_after: a.show_answers_after !== false,
      topic_rules: (a.topicRules || []).length
        ? a.topicRules.map((r) => ({
          professor_topic_id: r.professor_topic_id || "",
          topic_id: r.topic_id || "",
          num_questions: r.num_questions || 1,
          difficulty_filter: r.difficulty_filter || "",
        }))
        : [{ professor_topic_id: "", num_questions: 2, difficulty_filter: "" }],
    });
    setQuizDialog(true);
  };

  const addTopicRule = () => {
    setQuizForm((prev) => ({
      ...prev,
      topic_rules: [
        ...prev.topic_rules,
        { professor_topic_id: "", num_questions: 2, difficulty_filter: "" },
      ],
    }));
  };

  const updateTopicRule = (idx, field, value) => {
    setQuizForm((prev) => {
      const rules = [...prev.topic_rules];
      rules[idx] = { ...rules[idx], [field]: value };
      return { ...prev, topic_rules: rules };
    });
  };

  const removeTopicRule = (idx) => {
    if (quizForm.topic_rules.length <= 1) return;
    setQuizForm((prev) => ({
      ...prev,
      topic_rules: prev.topic_rules.filter((_, i) => i !== idx),
    }));
  };

  const saveQuiz = async () => {
    const payload = {
      ...quizForm,
      // Only send rules that have a topic (professor_topic_id or legacy topic_id)
      topic_rules: quizForm.topic_rules
        .map((r) => ({
          professor_topic_id: r.professor_topic_id || undefined,
          topic_id: r.topic_id || undefined,
          num_questions: r.num_questions,
          difficulty_filter: r.difficulty_filter,
        }))
        .filter((r) => r.professor_topic_id || r.topic_id),
    };
    if (editingQuiz) {
      await updateQuizAssignmentCall(editingQuiz.id, payload);
    } else {
      await createQuizAssignmentCall(payload);
    }
    setQuizDialog(false);
    loadAssignments();
  };

  const deleteQuiz = async (id) => {
    const ok = await openAppConfirm({
      title: "Delete quiz assignment?",
      message: "Delete this quiz assignment?",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    await deleteQuizAssignmentCall(id);
    loadAssignments();
  };

  // Validation helpers for disabling Save buttons
  const isQuestionValid = (() => {
    if (!questionForm.professor_topic_id || !questionForm.professor_tile_id) return false;
    if (!questionForm.question_content?.trim()) return false;
    if (!Array.isArray(questionForm.answers) || questionForm.answers.length !== 4) return false;
    const trimmedAnswers = questionForm.answers.map((a) => a.answer_text?.trim());
    if (trimmedAnswers.some((t) => !t)) return false;
    const correctCount = questionForm.answers.filter((a) => a.is_correct).length;
    return correctCount === 1;
  })();

  const isQuizValid = (() => {
    if (!quizForm.title?.trim()) return false;
    if (!hasMeaningfulHtmlContent(quizForm.rules_html)) return false;
    if (!Array.isArray(quizForm.topic_rules) || quizForm.topic_rules.length === 0) return false;
    const hasValidRule = quizForm.topic_rules.some(
      (r) =>
        (r.professor_topic_id || r.topic_id) &&
        Number(r.num_questions) > 0
    );
    return hasValidRule;
  })();

  const getProfTopicTitle = (id) => profTopics.find((t) => t.id === id)?.title ?? "—";
  const getProfTileTitle = (id) => profTiles.find((t) => t.id === id)?.title ?? "—";

  const looksLikeId = (s) => {
    if (!s || typeof s !== "string") return false;
    const trimmed = s.trim();
    return trimmed.length === 36 && /^[0-9a-f-]{36}$/i.test(trimmed) || trimmed === "";
  };
  const getQuestionDisplayTitle = (q) => {
    if (q.title && !looksLikeId(q.title)) return q.title;
    const content = (q.question_content || "").trim();
    return content ? content.slice(0, 60) + (content.length > 60 ? "…" : "") : "—";
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <RequestDemoDialog
        open={requestDemoOpen}
        onClose={() => setRequestDemoOpen(false)}
        defaultEmail={auth?.email || ""}
      />
      <MDBox py={3} width="100%">
        <MDBox width="100%" px={{ xs: 2, md: 3 }}>
          <MDBox
            mb={3}
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={2}
            flexWrap="wrap"
            justifyContent="space-between"
          >
            <MDBox display="flex" alignItems={{ xs: "flex-start", sm: "center" }} gap={2} flexWrap="wrap" flex={1} minWidth={0}>
              {canSelectProf && (
                <ProfSelector value={selectedProfId} onChange={setSelectedProfId} />
              )}
              {activeTab === 0 && (
                <MDBox
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                  }}
                >
                  <QuizOutlinedIcon sx={{ fontSize: 28 }} />
                </MDBox>
              )}
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold" sx={{ color: theme.palette.text.main }}>
                  {pageTitle}
                </MDTypography>
                <MDTypography variant="body2" sx={{ color: theme.palette.text.main, mt: 0.5, opacity: 0.9 }}>
                  {pageSubtitle}
                </MDTypography>
              </MDBox>
            </MDBox>
            {showRequestDemoCta && (
              <MDButton
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<EventAvailableRoundedIcon />}
                onClick={() => setRequestDemoOpen(true)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 2,
                  borderWidth: 1.5,
                  px: 2,
                  flexShrink: 0,
                  alignSelf: { xs: "stretch", sm: "center" },
                }}
              >
                Request a demo
              </MDButton>
            )}
          </MDBox>

          <Card
            sx={{
              width: "100%",
              borderRadius: 2,
              boxShadow: ({ shadows }) => shadows[4],
              overflow: "hidden",
            }}
          >
            {!isStandaloneStage && (
              <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                <Tab label={t("pages.quiz.myTitles")} />
                <Tab label={t("pages.quiz.questionBank")} />
                <Tab label={t("pages.quiz.createQuiz")} />
              </Tabs>
            )}

            <TabPanel value={activeTab} index={0}>
              <MDBox px={{ xs: 2, md: 3 }} py={3}>
                <MDBox
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  sx={{ mb: 3 }}
                >
                  <MDTypography variant="body2" sx={{ color: theme.palette.text.main, maxWidth: 560, opacity: 0.9 }}>
                    Add titles (sections) and topics under them. Questions and quizzes use these titles.
                  </MDTypography>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={() => openNewTitle("")}
                    startIcon={<AddRoundedIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 2,
                      boxShadow: theme.palette.mode === "dark" ? undefined : theme.shadows[2],
                      "&:hover": {
                        boxShadow: theme.palette.mode === "dark" ? undefined : theme.shadows[4],
                      },
                    }}
                  >
                    Add title or topic
                  </MDButton>
                </MDBox>

                {professorTitlesTree.length === 0 && !loading && (
                  <Card
                    variant="outlined"
                    sx={{
                      p: 4,
                      textAlign: "center",
                      borderRadius: 3,
                      borderStyle: "dashed",
                      borderWidth: 2,
                      borderColor: "divider",
                      bgcolor: "background.default",
                    }}
                  >
                    <MDBox sx={{ color: theme.palette.text.muted, mb: 2 }}>
                      <FolderOpenRoundedIcon sx={{ fontSize: 64, opacity: 0.5 }} />
                    </MDBox>
                    <MDTypography variant="h6" fontWeight="600" gutterBottom sx={{ color: theme.palette.text.main }}>
                      No titles yet
                    </MDTypography>
                    <MDTypography variant="body2" sx={{ color: theme.palette.text.main, mb: 2, maxWidth: 360, mx: "auto" }}>
                      Add a title first, then add topics under it. Your questions and quizzes will use this structure.
                    </MDTypography>
                    <MDButton
                      variant="contained"
                      color="primary"
                      onClick={() => openNewTitle("")}
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 2.5,
                      }}
                    >
                      Add your first title
                    </MDButton>
                  </Card>
                )}

                {professorTitlesTree.length > 0 && (
                  <MDBox display="flex" flexDirection="column" gap={2}>
                    {professorTitlesTree.map((tile) => (
                      <Card
                        key={tile.id}
                        variant="outlined"
                        sx={{
                          borderRadius: 2.5,
                          overflow: "hidden",
                          borderColor: "divider",
                          bgcolor: "background.default",
                          transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                          "&:hover": {
                            boxShadow: theme.shadows[2],
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          flexWrap="wrap"
                          gap={1}
                          p={2}
                          sx={{
                            borderBottom: (tile.children || []).length > 0 ? 1 : 0,
                            borderColor: "divider",
                          }}
                        >
                          <MDBox display="flex" alignItems="center" gap={1.5}>
                            <MDBox
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                              }}
                            >
                              <FolderOpenRoundedIcon fontSize="small" sx={{ color: "primary.main" }} />
                            </MDBox>
                            <MDTypography fontWeight="600" variant="body1" sx={{ color: theme.palette.text.main }}>
                              {tile.title}
                            </MDTypography>
                          </MDBox>
                          <MDBox display="flex" alignItems="center" gap={0.25}>
                            <Tooltip title="Add topic">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openNewTitle(tile.id)}
                                sx={{
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                  },
                                }}
                              >
                                <AddRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => openEditTitle(tile)}
                                sx={{
                                  color: "primary.main",
                                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" },
                                }}
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => deleteTitle(tile.id)}
                                sx={{ "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                              >
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </MDBox>
                        </MDBox>
                        {(tile.children || []).length > 0 && (
                          <MDBox sx={{ p: 1.5, pt: 0 }}>
                            {(tile.children || []).map((topic) => (
                              <MDBox
                                key={topic.id}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  py: 1.25,
                                  px: 2,
                                  borderRadius: 1.5,
                                  "&:hover": {
                                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                                  },
                                }}
                              >
                                <MDBox display="flex" alignItems="center" gap={1.5}>
                                  <LabelOutlinedIcon sx={{ fontSize: 20, color: "primary.main", opacity: 0.95 }} />
                                  <MDTypography variant="body2" fontWeight="500" sx={{ color: theme.palette.text.main }}>
                                    {topic.title}
                                  </MDTypography>
                                </MDBox>
                                <MDBox display="flex" alignItems="center" gap={0.25}>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => openEditTitle(topic)}
                                      sx={{
                                        color: "primary.main",
                                        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" },
                                      }}
                                    >
                                      <EditRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => deleteTitle(topic.id)}
                                      sx={{ "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.08) } }}
                                    >
                                      <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </MDBox>
                              </MDBox>
                            ))}
                          </MDBox>
                        )}
                      </Card>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <MDBox px={2} pb={2}>
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                  <Grid item>
                    <FormControl sx={{ minWidth: 160, ...INPUT_HEIGHT_SX }}>
                      <InputLabel>Topic</InputLabel>
                      <Select
                        value={filters.professor_topic_id}
                        label="Topic"
                        onChange={(e) => setFilters((f) => ({ ...f, professor_topic_id: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {profTopics.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl sx={{ minWidth: 160, ...INPUT_HEIGHT_SX }}>
                      <InputLabel>Title</InputLabel>
                      <Select
                        value={filters.professor_tile_id}
                        label="Title"
                        onChange={(e) => setFilters((f) => ({ ...f, professor_tile_id: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {profTiles.map((t) => (
                          <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <FormControl sx={{ minWidth: 120, ...INPUT_HEIGHT_SX }}>
                      <InputLabel>Difficulty</InputLabel>
                      <Select
                        value={filters.difficulty}
                        label="Difficulty"
                        onChange={(e) => setFilters((f) => ({ ...f, difficulty: e.target.value }))}
                      >
                        <MenuItem value="">All</MenuItem>
                        {DIFFICULTIES.map((d) => (
                          <MenuItem key={d} value={d}>{d}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <MDButton
                      variant="gradient"
                      color="info"
                      onClick={openNewQuestion}
                      sx={{ color: "white !important" }}
                    >
                      + Add Question
                    </MDButton>
                  </Grid>
                </Grid>

                {loading ? (
                  <MDTypography sx={{ color: theme.palette.text.main }}>Loading...</MDTypography>
                ) : (
                  <>
                    <TableContainer sx={{ width: "100%", bgcolor: "transparent", boxShadow: "none" }}>
                      <Table size="small" sx={{ width: "100%", tableLayout: "fixed", "& .MuiTableCell-root": { color: theme.palette.text.main } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Title / Content</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Difficulty</TableCell>
                            <TableCell>Topic</TableCell>
                            <TableCell>Title</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(questionsRowsPerPage > 0
                            ? questions.slice(
                              questionsPage * questionsRowsPerPage,
                              questionsPage * questionsRowsPerPage + questionsRowsPerPage
                            )
                            : questions
                          ).map((q) => (
                            <TableRow key={q.id}>
                              <TableCell>
                                {getQuestionDisplayTitle(q)}
                              </TableCell>
                              <TableCell>{q.question_type}</TableCell>
                              <TableCell><Chip size="small" label={q.difficulty} /></TableCell>
                              <TableCell>{getProfTopicTitle(q.professor_topic_id)}</TableCell>
                              <TableCell>{getProfTileTitle(q.professor_tile_id)}</TableCell>
                              <TableCell align="right" padding="none">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setQuestionRowMenuAnchor(e.currentTarget);
                                    setQuestionRowMenuQuestion(q);
                                  }}
                                  sx={{ color: "text.secondary" }}
                                  aria-label="Question actions"
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={questions.length}
                      rowsPerPage={questionsRowsPerPage}
                      page={questionsPage}
                      onPageChange={(_, newPage) => setQuestionsPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setQuestionsRowsPerPage(parseInt(e.target.value, 10));
                        setQuestionsPage(0);
                      }}
                      labelRowsPerPage="Rows per page:"
                      sx={{ color: theme.palette.text.main }}
                    />
                    <Menu
                      anchorEl={questionRowMenuAnchor}
                      open={Boolean(questionRowMenuAnchor)}
                      onClose={() => {
                        setQuestionRowMenuAnchor(null);
                        setQuestionRowMenuQuestion(null);
                      }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          if (questionRowMenuQuestion) openEditQuestion(questionRowMenuQuestion);
                          setQuestionRowMenuAnchor(null);
                          setQuestionRowMenuQuestion(null);
                        }}
                      >
                        <EditRoundedIcon sx={{ mr: 1, fontSize: 20 }} /> Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (questionRowMenuQuestion?.id) deleteQuestion(questionRowMenuQuestion.id);
                          setQuestionRowMenuAnchor(null);
                          setQuestionRowMenuQuestion(null);
                        }}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineRoundedIcon sx={{ mr: 1, fontSize: 20 }} /> Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </MDBox>
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <MDBox px={2} pb={2} sx={{ bgcolor: "transparent", boxShadow: "none" }}>
                <MDBox mb={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={openNewQuiz}
                    sx={{ color: "white !important" }}
                  >
                    + Create Quiz
                  </MDButton>
                </MDBox>
                {loading ? (
                  <MDTypography sx={{ color: theme.palette.text.main }}>Loading...</MDTypography>
                ) : (
                  <>
                    <TableContainer sx={{ width: "100%", bgcolor: "transparent", boxShadow: "none" }}>
                      <Table size="small" sx={{ width: "100%", minWidth: 600, tableLayout: "fixed", "& .MuiTableCell-root": { color: theme.palette.text.main } }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>Time limit</TableCell>
                            <TableCell>Topic rules</TableCell>
                            <TableCell>Code</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(assignmentsRowsPerPage > 0
                            ? assignments.slice(
                              assignmentsPage * assignmentsRowsPerPage,
                              assignmentsPage * assignmentsRowsPerPage + assignmentsRowsPerPage
                            )
                            : assignments
                          ).map((a) => (
                            <TableRow key={a.id}>
                              <TableCell>{a.title}</TableCell>
                              <TableCell>{a.time_limit_minutes} min</TableCell>
                              <TableCell>
                                {(a.topicRules || []).map((r) => (
                                  <Chip
                                    key={r.id}
                                    size="small"
                                    sx={{ mr: 0.5, color: theme.palette.text.main, "& .MuiChip-label": { color: "inherit" } }}
                                    label={`${getProfTopicTitle(r.professor_topic_id) || getProfTopicTitle(r.topic_id)}: ${r.num_questions}`}
                                  />
                                ))}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  const raw = a.access_code || a.id || "";
                                  if (!raw) return "-";
                                  const displayLabel =
                                    raw.length > 12
                                      ? raw.replace(/-/g, "").slice(0, 8).toUpperCase() + "…"
                                      : raw;
                                  return (
                                    <MDBox display="flex" alignItems="center" gap={1}>
                                      <Tooltip title={raw}>
                                        <MDTypography variant="body2" sx={{ cursor: "default" }}>
                                          {displayLabel}
                                        </MDTypography>
                                      </Tooltip>
                                      <Tooltip title="Show QR code (students scan to join)">
                                        <IconButton
                                          size="small"
                                          onClick={() => openQrDialogForCode(raw)}
                                          sx={{ color: "text.secondary" }}
                                          aria-label="Show QR for this code"
                                        >
                                          <QrCode2Icon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </MDBox>
                                  );
                                })()}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAssignmentMenuAnchor(e.currentTarget);
                                    setAssignmentMenuRow(a);
                                  }}
                                  aria-label="Open menu"
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={assignments.length}
                      rowsPerPage={assignmentsRowsPerPage}
                      page={assignmentsPage}
                      onPageChange={(_, newPage) => setAssignmentsPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setAssignmentsRowsPerPage(parseInt(e.target.value, 10));
                        setAssignmentsPage(0);
                      }}
                      labelRowsPerPage="Rows per page:"
                      sx={{ color: theme.palette.text.main }}
                    />
                    <Menu
                      anchorEl={assignmentMenuAnchor}
                      open={Boolean(assignmentMenuAnchor)}
                      onClose={() => { setAssignmentMenuAnchor(null); setAssignmentMenuRow(null); }}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                    >
                      <MenuItem
                        onClick={() => {
                          if (assignmentMenuRow) openEditQuiz(assignmentMenuRow);
                          setAssignmentMenuAnchor(null);
                          setAssignmentMenuRow(null);
                        }}
                      >
                        Edit
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          if (assignmentMenuRow) {
                            deleteQuiz(assignmentMenuRow.id);
                          }
                          setAssignmentMenuAnchor(null);
                          setAssignmentMenuRow(null);
                        }}
                        sx={{ color: "error.main" }}
                      >
                        Delete
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </MDBox>
            </TabPanel>
          </Card>
        </MDBox>
      </MDBox>

      {/* Question dialog */}
      <Dialog
        open={questionDialog}
        onClose={() => {
          setQuestionDialog(false);
          setDialogTransitionDone(false);
        }}
        maxWidth="md"
        fullWidth
        TransitionProps={{
          onEntered: () => setDialogTransitionDone(true),
        }}
        PaperProps={{
          sx: { bgcolor: "background.card", color: theme.palette.text.main },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.main }}>{editingQuestion ? "Edit Question" : "Add Question"}</DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question title"
                value={questionForm.title}
                onChange={(e) =>
                  setQuestionForm((f) => ({ ...f, title: e.target.value }))
                }
                placeholder="Short label for this question"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={questionForm.question_type}
                  label="Type"
                  onChange={(e) => setQuestionForm((f) => ({ ...f, question_type: e.target.value }))}
                >
                  {QUESTION_TYPES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel>Difficulty</InputLabel>
                <Select
                  value={questionForm.difficulty}
                  label="Difficulty"
                  onChange={(e) => setQuestionForm((f) => ({ ...f, difficulty: e.target.value }))}
                >
                  {DIFFICULTIES.map((d) => (
                    <MenuItem key={d} value={d}>{d}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel required>Title</InputLabel>
                <Select
                  value={questionForm.professor_tile_id}
                  label="Title"
                  onChange={(e) => {
                    const tileId = e.target.value || "";
                    const tile = profTiles.find((t) => t.id === tileId);
                    const topicId =
                      tile?.professor_topic_id ||
                      questionForm.professor_topic_id ||
                      "";
                    setQuestionForm((f) => ({
                      ...f,
                      professor_tile_id: tileId,
                      professor_topic_id: topicId,
                    }));
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {profTiles.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_HEIGHT_SX}>
                <InputLabel required>Topic</InputLabel>
                <Select
                  value={questionForm.professor_topic_id}
                  label="Topic"
                  onChange={(e) => {
                    const topicId = e.target.value || "";
                    setQuestionForm((f) => ({
                      ...f,
                      professor_topic_id: topicId,
                    }));
                  }}
                >
                  <MenuItem value="">—</MenuItem>
                  {profTopics
                    .filter((t) => {
                      // If a title is selected and it has a linked topic, only show that topic
                      const selectedTile = profTiles.find(
                        (tile) => tile.id === questionForm.professor_tile_id
                      );
                      const linkedTopicId = selectedTile?.professor_topic_id;
                      if (linkedTopicId) {
                        return t.id === linkedTopicId;
                      }
                      // Otherwise show all topics
                      return true;
                    })
                    .map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.title}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Image based: upload a photo for the question */}
            {questionForm.question_type === "image_based" && (
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="medium" sx={{ color: theme.palette.text.main, display: "block", mb: 1 }}>
                  Upload a photo for this question
                </MDTypography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, alignItems: "flex-start" }}>
                  <input
                    ref={questionImageInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setQuestionImageUploading(true);
                      try {
                        const url = await UploadMyFileCall(file, "user_upload");
                        if (url) {
                          setQuestionForm((f) => ({ ...f, question_image_url: url }));
                        }
                      } catch (err) {
                        console.error("Question image upload failed:", err);
                      } finally {
                        setQuestionImageUploading(false);
                        if (questionImageInputRef.current) questionImageInputRef.current.value = "";
                      }
                    }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    component="span"
                    disabled={questionImageUploading}
                    onClick={() => questionImageInputRef.current?.click()}
                    sx={{ textTransform: "none" }}
                  >
                    {questionImageUploading ? "Uploading…" : "Choose photo"}
                  </Button>
                  {questionForm.question_image_url && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Box
                        component="img"
                        src={questionForm.question_image_url}
                        alt="Question"
                        sx={{ maxWidth: 200, maxHeight: 120, objectFit: "contain", borderRadius: 1, border: "1px solid", borderColor: "divider" }}
                      />
                      <Button
                        size="small"
                        color="secondary"
                        onClick={() => setQuestionForm((f) => ({ ...f, question_image_url: "" }))}
                        sx={{ textTransform: "none" }}
                      >
                        Remove photo
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            {/* AI Assist — after topic, title, and type for better results */}
            <Grid item xs={12}>
              <MDBox
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  overflow: "hidden",
                }}
              >
                <Button
                  fullWidth
                  onClick={() => setAiAssistExpanded((e) => !e)}
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    py: 1.5,
                    px: 2,
                    color: theme.palette.text.main,
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08) },
                  }}
                >
                  <AutoAwesomeRoundedIcon sx={{ mr: 1, color: "primary.main" }} />
                  <MDTypography variant="button" fontWeight="medium">AI Assist — generate a question suggestion</MDTypography>
                  <Box sx={{ flex: 1 }} />
                  <MDTypography variant="caption" sx={{ color: "text.secondary" }}>{aiAssistExpanded ? "Hide" : "Show"}</MDTypography>
                </Button>
                <Collapse in={aiAssistExpanded}>
                  <MDBox sx={{ px: 2, pb: 2, pt: 0 }}>
                    <MDTypography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
                      For better results, select the topic, title, and question type above first, then use AI Assist.
                    </MDTypography>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      placeholder="Optional: describe what you want (e.g. “Ask about the structure at point A” or leave empty for a general question)"
                      value={aiPrompt}
                      onChange={(e) => { setAiPrompt(e.target.value); setAiError(null); }}
                      size="small"
                      sx={{ mb: 1.5, "& .MuiInputBase-root": { bgcolor: "background.paper" } }}
                    />
                    {aiError && (
                      <MDTypography variant="caption" sx={{ color: "error.main", display: "block", mb: 1 }}>{aiError}</MDTypography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleGenerateQuestion}
                      disabled={aiLoading}
                      startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeRoundedIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      {aiLoading ? "Generating…" : "Generate suggestion"}
                    </Button>
                    <MDTypography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                      Uses the selected topic, title, and slide (if any) plus your prompt to fill the form below.
                    </MDTypography>
                  </MDBox>
                </Collapse>
              </MDBox>
            </Grid>

            {/* Slide / 3D model picker: only for slide_referenced; hidden for text_only and image_based */}
            {questionForm.question_type === "slide_referenced" && (
              <>
                <Grid item xs={12}>
                  <MDTypography variant="caption" fontWeight="medium" sx={{ color: theme.palette.text.main, display: "block", mb: 1 }}>
                    Slide or 3D model reference — search and select a slide (Histology, Embryology) or 3D model
                  </MDTypography>
                  <Autocomplete
                    options={publicSlideEntries}
                    getOptionLabel={(opt) => (opt?.label != null ? opt.label : "")}
                    value={selectedSlideEntry || null}
                    onChange={(e, val) => {
                      setQuestionForm((f) => ({
                        ...f,
                        tile_id: val?.tileId ?? "",
                        slide_id: val?.slideId ?? "",
                        question_image_url: val
                          ? (val.is3D && val.modelUrl ? val.modelUrl : getSlideTileUrl(val))
                          : "",
                        pins: val?.is3D ? [] : (f.pins || []),
                      }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search slides or 3D models…"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">🔍</InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, opt) => (
                      <li {...props} key={`${opt.tileId}-${opt.slideId}`}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          {opt.is3D && opt.modelUrl ? (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1,
                                overflow: "hidden",
                                flexShrink: 0,
                                bgcolor: "grey.200",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "grey.600",
                              }}
                            >
                              3D
                            </Box>
                          ) : (
                            <Box
                              component="img"
                              src={opt.imageUrl || noImageUrl}
                              alt=""
                              sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
                            />
                          )}
                          <span>{opt.label}</span>
                        </Box>
                      </li>
                    )}
                  />
                </Grid>
                {(selectedSlideEntry || questionForm.question_image_url) && (
                  <Grid item xs={12}>
                    {(() => {
                      const is3DSlide =
                        selectedSlideEntry?.is3D ||
                        looksLike3DUrl(questionForm.question_image_url);
                      const modelUrl = is3DSlide
                        ? (selectedSlideEntry?.modelUrl || questionForm.question_image_url)
                        : null;
                      return (
                        <>
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
                            <MDTypography variant="caption" sx={{ color: theme.palette.text.main }} fontWeight="medium">
                              {is3DSlide
                                ? "Selected 3D model — use the viewer below"
                                : "Selected slide — add or edit pins (saved with the question for quiz sessions)"}
                            </MDTypography>
                            {!is3DSlide && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                onClick={() => setQuestionForm((f) => ({ ...f, pins: [] }))}
                                disabled={!questionForm.pins?.length}
                              >
                                Clear pins
                              </Button>
                            )}
                          </Box>
                          {is3DSlide && modelUrl ? (
                            <MDBox
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                overflow: "hidden",
                                height: 380,
                                bgcolor: "grey.100",
                              }}
                            >
                              <iframe
                                src={`${typeof window !== "undefined" ? window.location.origin : ""}/model-viewer.html?src=${encodeURIComponent(modelUrl)}&theme=${darkMode ? "dark" : "light"}`}
                                title="3D Model preview"
                                style={{ width: "100%", height: "100%", minHeight: 360, border: "none", display: "block" }}
                              />
                            </MDBox>
                          ) : !is3DSlide ? (
                            questionForm.question_image_url ? (
                              <MDBox
                                key={`qb-slide-map-${editingQuestion?.id ?? "new"}-${(questionForm.question_image_url || "").slice(0, 40)}`}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  overflow: "hidden",
                                  height: 380,
                                  minHeight: 380,
                                }}
                              >
                                <div
                                  id="question-bank-slide-map"
                                  style={{ width: "100%", height: 380, minHeight: 380 }}
                                />
                              </MDBox>
                            ) : (
                              <MDBox
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  height: 380,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  bgcolor: "grey.100",
                                }}
                              >
                                <MDTypography variant="body2" sx={{ color: theme.palette.text.main }}>
                                  Slide preview not available for this slide. You can still save the question.
                                </MDTypography>
                              </MDBox>
                            )
                          ) : null}
                          {selectedSlideEntry && (
                            <MDTypography variant="caption" sx={{ color: theme.palette.text.main, display: "block", mt: 1 }}>{selectedSlideEntry.label}</MDTypography>
                          )}
                        </>
                      );
                    })()}
                  </Grid>
                )}
              </>
            )}

            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Question content *" value={questionForm.question_content} onChange={(e) => setQuestionForm((f) => ({ ...f, question_content: e.target.value }))} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Explanation (optional)" value={questionForm.explanation} onChange={(e) => setQuestionForm((f) => ({ ...f, explanation: e.target.value }))} />
            </Grid>
            {questionForm.answers.map((a, idx) => (
              <Grid item xs={12} key={idx}>
                <MDBox display="flex" alignItems="center" gap={1}>
                  <Chip size="small" color={a.is_correct ? "success" : "default"} label={`${["A", "B", "C", "D"][idx]} (correct)`} onClick={() => setAnswerCorrect(idx)} />
                  <TextField
                    fullWidth
                    size="small"
                    label={`Answer ${["A", "B", "C", "D"][idx]}`}
                    value={a.answer_text}
                    onChange={(e) =>
                      setQuestionForm((f) => ({
                        ...f,
                        answers: f.answers.map((x, i) => (i === idx ? { ...x, answer_text: e.target.value } : x)),
                      }))
                    }
                  />
                </MDBox>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ color: theme.palette.text.main }}>
          <Button onClick={() => setQuestionDialog(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={saveQuestion}
            disabled={!isQuestionValid}
            sx={{ color: "white" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quiz dialog */}
      <Dialog
        open={quizDialog}
        onClose={() => setQuizDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "background.card", color: theme.palette.text.main },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.main }}>{editingQuiz ? "Edit Quiz" : "Create Quiz"}</DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Quiz title *" value={quizForm.title} onChange={(e) => setQuizForm((f) => ({ ...f, title: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField type="number" fullWidth label="Time limit (min)" value={quizForm.time_limit_minutes} onChange={(e) => setQuizForm((f) => ({ ...f, time_limit_minutes: Number(e.target.value) || 30 }))} />
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="caption" sx={{ color: theme.palette.text.main }}>
                Quiz rules (HTML) * (required). This content is shown to students before starting a university quiz.
              </MDTypography>
              <MDBox
                sx={{
                  mt: 1,
                  borderRadius: 1,
                  overflow: "hidden",
                  "& .ql-toolbar.ql-snow": {
                    borderColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.23)",
                    backgroundColor: darkMode ? "#1f1f1f" : "#fafafa",
                  },
                  "& .ql-container.ql-snow": {
                    borderColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.23)",
                    backgroundColor: darkMode ? "#121212" : "#fff",
                    minHeight: 220,
                  },
                  "& .ql-editor": {
                    color: theme.palette.text.main,
                    fontSize: 14,
                    lineHeight: 1.6,
                  },
                }}
              >
                <ReactQuill
                  theme="snow"
                  value={quizForm.rules_html || ""}
                  onChange={(value) =>
                    setQuizForm((f) => ({
                      ...f,
                      rules_html: value,
                    }))
                  }
                  modules={QUIZ_RULES_EDITOR_MODULES}
                  formats={QUIZ_RULES_EDITOR_FORMATS}
                  placeholder="Write and format quiz rules..."
                />
              </MDBox>
              {!hasMeaningfulHtmlContent(quizForm.rules_html) && (
                <MDTypography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: 0.75 }}
                >
                  Quiz rules are required.
                </MDTypography>
              )}
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="caption" sx={{ color: theme.palette.text.main }}>
                Questions per topic
              </MDTypography>
              {quizForm.topic_rules.map((r, idx) => (
                <MDBox key={idx} display="flex" alignItems="center" gap={1} mt={1}>
                  <FormControl sx={{ minWidth: 200, ...INPUT_HEIGHT_SX }}>
                    <InputLabel>Topic</InputLabel>
                    <Select
                      value={r.professor_topic_id || ""}
                      label="Topic"
                      onChange={(e) =>
                        updateTopicRule(idx, "professor_topic_id", e.target.value || "")
                      }
                    >
                      <MenuItem value="">—</MenuItem>
                      {profTopics.map((t) => (
                        <MenuItem key={t.id} value={t.id}>
                          {t.title}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    type="number"
                    sx={{ width: 80, "& .MuiInputBase-root": { minHeight: 44 } }}
                    value={r.num_questions}
                    onChange={(e) => updateTopicRule(idx, "num_questions", Number(e.target.value) || 1)}
                  />
                  <FormControl sx={{ minWidth: 140, ...INPUT_HEIGHT_SX }}>
                    <InputLabel>Level</InputLabel>
                    <Select
                      value={r.difficulty_filter || ""}
                      label="Level"
                      onChange={(e) =>
                        updateTopicRule(idx, "difficulty_filter", e.target.value || "")
                      }
                    >
                      <MenuItem value="">Any</MenuItem>
                      {DIFFICULTIES.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton size="small" onClick={() => removeTopicRule(idx)} disabled={quizForm.topic_rules.length <= 1}>−</IconButton>
                </MDBox>
              ))}
              <Button size="small" onClick={addTopicRule}>+ Add topic rule</Button>
            </Grid>
            <Grid item xs={12}>
              <MDTypography variant="caption" sx={{ color: theme.palette.text.main }}>
                Share the quiz code with students so they can join this quiz.
              </MDTypography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ color: theme.palette.text.main }}>
          <Button onClick={() => setQuizDialog(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={saveQuiz}
            disabled={!isQuizValid}
            sx={{ color: "white" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR code dialog for quiz access code */}
      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "background.default",
            color: theme.palette.text.main,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          Quiz access QR code
        </DialogTitle>
        <DialogContent>
          <MDTypography
            variant="body2"
            fontWeight="bold"
            color="error"
            sx={{ mb: 2, textAlign: "center" }}
          >
            IMPORTANT: For the QR to auto-open the quiz and fill the code, the
            Microlab app must be closed (killed) before scanning.
          </MDTypography>
          <MDBox
            mt={4}
            mb={2}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
          >
            {qrDialogCode && (
              <Box
                sx={{
                  width: 280,
                  height: 280,
                  maxWidth: "80vw",
                  maxHeight: "80vw",
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "background.paper",
                  borderRadius: 2,
                  boxShadow: 4,
                  p: 2,
                }}
              >
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(
                    qrDialogLink || qrDialogCode
                  )}`}
                  alt="Quiz QR code"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            )}
            <MDTypography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
              {qrDialogCode}
            </MDTypography>
            <MDTypography
              variant="body2"
              sx={{ textAlign: "center", maxWidth: 360, mt: 1 }}
            >
              Ask students to scan this QR code with their phone camera. It
              opens the Microlab quiz join screen and uses this code
              automatically.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button onClick={() => setQrDialogOpen(false)} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Title dialog (title or topic) */}
      <Dialog
        open={titleDialog}
        onClose={() => setTitleDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "background.card", color: theme.palette.text.main },
        }}
      >
        <DialogTitle sx={{ color: theme.palette.text.main }}>{editingTitle ? "Edit Title" : "Add Title / Topic"}</DialogTitle>
        <DialogContent sx={{ color: theme.palette.text.main }}>
          <MDBox pt={1}>
            {!editingTitle && (
              <FormControl fullWidth sx={{ mb: 2, ...INPUT_HEIGHT_SX }}>
                <InputLabel>Under title (optional)</InputLabel>
                <Select
                  value={titleForm.parent_id}
                  label="Under title (optional)"
                  onChange={(e) => setTitleForm((f) => ({ ...f, parent_id: e.target.value }))}
                >
                  <MenuItem value="">New title (no parent)</MenuItem>
                  {profTiles.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.title}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              fullWidth
              label="Title"
              value={titleForm.title}
              onChange={(e) => setTitleForm((f) => ({ ...f, title: e.target.value }))}
              placeholder={titleForm.parent_id ? "Topic name" : "Title name"}
            />
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ color: theme.palette.text.main }}>
          <Button onClick={() => setTitleDialog(false)} color="inherit">Cancel</Button>
          <Button
            variant="contained"
            onClick={saveTitle}
            disabled={!titleForm.title?.trim()}
            sx={{ color: "white" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}
